import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { TECHNOLOGY_AREAS, TECHNOLOGY_AREA_LABEL_MAP } from '@/lib/constants';

type Props = {
  selected: string[];
  onChange: (values: string[]) => void;
  max?: number;
  error?: string;
  label?: string;
  description?: string;
};

export default function TechnologyAreaPicker({
  selected,
  onChange,
  max = 5,
  error,
  label = 'Technology Areas *',
  description,
}: Props) {
  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else if (selected.length < max) {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">{label}</h2>
      <p className="text-sm font-bold text-foreground">
        How do you want to participate? Select all that apply.
      </p>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      {!description && <p className="text-xs text-muted-foreground">Select up to {max}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="grid grid-cols-2 gap-2">
        {TECHNOLOGY_AREAS.map(({ value, label: areaLabel }) => {
          const isSelected = selected.includes(value);
          const isDisabled = !isSelected && selected.length >= max;
          return (
            <label
              key={value}
              className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isSelected
                  ? 'border-primary bg-primary/5'
                  : isDisabled
                    ? 'border-border opacity-50 cursor-not-allowed'
                    : 'border-border hover:border-primary/50'
              }`}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => !isDisabled && toggle(value)}
                disabled={isDisabled}
                className="h-4 w-4"
              />
              <span className="text-xs text-foreground flex-1">{areaLabel}</span>
            </label>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="pt-1">
          <p className="text-xs text-muted-foreground mb-1">Selected ({selected.length}/{max}):</p>
          <div className="flex flex-wrap gap-1.5">
            {selected.map((v) => (
              <Badge key={v} variant="secondary" className="text-xs px-2 py-0.5">
                {TECHNOLOGY_AREA_LABEL_MAP[v] || v}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
